"use client";

import { useTranslation } from "@/lib/i18n";
import type {
  CustomSection,
  CustomSectionGroup,
  Language,
  Profile,
  Reference,
  SectionWithItem,
  Skill,
} from "@/lib/schema/sections";
import { TResume } from "@/lib/store/resume";
import { formatPostalCodeWithPadding, isUrl } from "@/utils";
import { isEmptyString } from "@/utils/preview";
import classNames from "classnames";
import { get } from "lodash";
import { Fragment } from "react";
import { TemplateProps } from ".";
import { LevelMap } from "../sidebars/left/sections/Languages";
import { Picture } from "../sidebars/left/sections/shared/Picture";

const TDCellContent = (props: {
  label: string;
  value?: string;
  valueClassNames?: string;
}) => {
  const { label, value, valueClassNames } = props;
  return (
    <div className="relative flex h-full w-full items-center justify-center">
      <span className="absolute left-0 top-0 text-base">{label}</span>
      <span className={classNames("text-base", valueClassNames)}>{value}</span>
    </div>
  );
};

const Header = ({ resume }: { resume: TResume }) => {
  const { t } = useTranslation({ ns: "resume-sidebar" });

  const basics = resume.data.basics;

  if (!basics) return null;

  return (
    <div className="relative w-full">
      <div className="mb-2 flex w-[520px] items-end justify-between">
        <p className="text-3xl font-bold">履歴書</p>
        <p className="text-base">{basics.creationDate}</p>
      </div>
      <table className="w-full table-fixed">
        <tbody>
          <tr>
            <td colSpan={2} className="h-[40px] w-[550px] text-base">
              <div className="relative flex h-full items-center justify-center">
                <span className="absolute left-0 text-base">ふりがな</span>
                <span className="text-base">{basics.furigana}</span>
              </div>
            </td>
            <td className="!border-none"></td>
          </tr>
          <tr>
            <td colSpan={2} className="h-[60px] w-[550px]">
              <TDCellContent
                label="氏名"
                value={basics.name}
                valueClassNames="!text-2xl font-bold"
              />
            </td>
            <td className="!border-none"></td>
          </tr>
          <tr>
            <td colSpan={2} className="h-[40px] !p-0 text-center text-lg">
              <div className="flex h-full w-full ">
                <div
                  className="h-full w-[450px]"
                  style={{
                    padding: "5px",
                  }}
                >
                  {basics.birthdate}
                </div>
                <div
                  className="flex h-full w-full flex-1 items-center"
                  style={{
                    padding: "5px",
                    borderLeft: "1px solid rgb(15 23 42)",
                  }}
                >
                  <TDCellContent
                    label={"性别"}
                    value={basics.gender}
                    valueClassNames="!text-lg"
                  />
                </div>
              </div>
            </td>
            <td className="!border-none"></td>
          </tr>
          <tr>
            <td colSpan={2} className="h-[40px] w-[600px]">
              <TDCellContent label="Email" value={basics.email} />
            </td>
            <td className="h-[40px]">
              <TDCellContent label="電話" value={basics.phone} />
            </td>
          </tr>
          {basics?.customFields?.map((item) => (
            <tr key={item.id}>
              <td colSpan={3} className="h-[40px] w-full">
                <TDCellContent label={item.name} value={item.value} />
              </td>
            </tr>
          ))}
          {basics.website?.href && (
            <tr>
              <td colSpan={3} className="h-[40px] w-full">
                <TDCellContent
                  label="ウェブサイト"
                  value={basics.website?.href}
                />
              </td>
            </tr>
          )}
          <tr>
            <td colSpan={3} className="h-[40px] w-full text-base">
              <div className="relative flex h-full items-center justify-center">
                <span className="absolute left-0 text-base">ふりがな</span>
                <span className="text-base">{basics.locationFurigana}</span>
              </div>
            </td>
          </tr>
          <tr>
            <td colSpan={3} className="h-[60px] w-full">
              <TDCellContent
                label={`現住所   〒
                ${formatPostalCodeWithPadding(basics.postalCode)}`}
                value={basics.location}
              />
            </td>
          </tr>
          <tr>
            <td colSpan={3} className="h-[40px] w-full text-base">
              <div className="relative flex h-full items-center justify-center">
                <span className="absolute left-0 text-base">ふりがな</span>
                <span className="text-base">
                  {basics.contactLocationFurigana}
                </span>
              </div>
            </td>
          </tr>
          <tr>
            <td colSpan={3} className="h-[60px] w-full">
              <TDCellContent
                label={`連絡先   〒
                ${formatPostalCodeWithPadding(
                  basics.contactLocationPostalCode,
                )}`}
                value={basics.contactLocation}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <Picture
        resume={resume}
        className="!absolute right-10 top-0 h-[168px] w-[120px]"
      />
    </div>
  );
};

const Summary = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.summary;
  if (!section?.visible || isEmptyString(section?.content)) return null;

  return (
    <table className="w-full table-fixed">
      <tbody>
        <tr>
          <td className="h-[30px] text-left text-lg">{section?.name}</td>
        </tr>
        <tr>
          <td>
            <div dangerouslySetInnerHTML={{ __html: section?.content || "" }} />
          </td>
        </tr>
      </tbody>
    </table>
  );

  return (
    <section id={section?.id}>
      <h4 className="text-resume-primary border-resume-primary mb-2 border-b-2 pb-0.5 text-2xl font-bold">
        {section?.name}
      </h4>

      <div dangerouslySetInnerHTML={{ __html: section?.content || "" }} />
    </section>
  );
};

type RatingProps = { level: number };

const Rating = ({ level }: RatingProps) => (
  <div className="flex items-center gap-x-1.5">
    {Array.from({ length: 5 }).map((_, index) => (
      <div
        key={index}
        className={classNames(
          "border-resume-primary h-2 w-2 rounded-full border",
          level > index && "bg-resume-primary",
        )}
      />
    ))}
  </div>
);

type SectionProps<T> = {
  section?: SectionWithItem<T> | CustomSectionGroup;
  children?: (item: T) => React.ReactNode;
  className?: string;
  urlKey?: keyof T;
  levelKey?: keyof T;
  summaryKey?: keyof T;
  keywordsKey?: keyof T;
};

const Section = <T,>({
  section,
  children,
  className,
  urlKey,
  levelKey,
  summaryKey,
  keywordsKey,
}: SectionProps<T>) => {
  if (!section?.visible || !section?.items?.length) return null;

  return (
    <section id={section.id} className="grid">
      <h4 className="text-resume-primary border-resume-primary mb-2  border-b-2 pb-0.5 text-2xl font-bold">
        {section.name}
      </h4>

      <div
        className="grid gap-x-6 gap-y-3"
        style={{ gridTemplateColumns: `repeat(1, 1fr)` }}
      >
        {section.items
          .filter((item) => item.visible)
          .map((item) => {
            const url = urlKey && get(item, urlKey);
            const level = LevelMap[levelKey && get(item, levelKey, "basic")];

            const summary = (summaryKey && get(item, summaryKey, "")) as
              | string
              | undefined;
            const keywords = (keywordsKey && get(item, keywordsKey, [])) as
              | string[]
              | undefined;
            return (
              <div key={item.id} className={classNames("space-y-2", className)}>
                <div>
                  {children?.(item as T)}
                  {url?.href && (
                    <a target="_blank" rel="noreferrer" href={url?.href}>
                      {url?.href}
                    </a>
                  )}
                </div>

                {summary !== undefined && !isEmptyString(summary) && (
                  <div dangerouslySetInnerHTML={{ __html: summary }} />
                )}

                {level !== undefined && level > 0 && <Rating level={level} />}

                {keywords !== undefined && keywords.length > 0 && (
                  <p className="text-sm">{keywords.join(", ")}</p>
                )}
              </div>
            );
          })}
      </div>
    </section>
  );
};

const Profiles = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.profiles;
  const fontSize = resume.data.settings?.typography.font.size;

  return (
    <Section<Profile> section={section}>
      {(item) => (
        <div>
          {isUrl(item.url?.href) ? (
            <a href={item.url?.href}>
              <img
                className="resume-icon mr-1"
                width={fontSize}
                height={fontSize}
                alt={item.network}
                src={`https://cdn.simpleicons.org/${item.icon}`}
              />
              {item.username}
            </a>
          ) : (
            <div>{item.username}</div>
          )}
          {item.url?.href && (
            <a
              className="text-sm"
              target="_blank"
              rel="noreferrer"
              href={item.url?.href}
            >
              {item.url?.href}
            </a>
          )}
        </div>
      )}
    </Section>
  );
};

const WorkExperience = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.workExperience;
  if (!section?.visible || !section?.items?.length) return null;
  return (
    <table className="w-full table-fixed">
      <tbody>
        <tr>
          <td className="h-[30px] w-[250px] text-center text-lg">年月</td>
          <td className="h-[30px] text-center text-lg">{section?.name}</td>
        </tr>
        {section?.items?.map((res) => {
          return (
            <tr key={res.id}>
              <td className="w-[250px] text-center text-base">{res.date}</td>
              <td>
                <div dangerouslySetInnerHTML={{ __html: res.summary || "" }} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  // return (
  //   <Section<Experience> section={section} urlKey="url" summaryKey="summary">
  //     {(item) => (
  //       <div className="flex items-center justify-between">
  //         <div className="text-left">
  //           <div className="text-base font-bold">{item.company}</div>
  //           <div>{item.position}</div>
  //         </div>

  //         <div className="shrink-0 text-right">
  //           <div className="font-bold">{item.date}</div>
  //           <div>{item.location}</div>
  //         </div>
  //       </div>
  //     )}
  //   </Section>
  // );
};

const Internship = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.internship;
  if (!section?.visible || !section?.items?.length) return null;
  return (
    <table className="w-full table-fixed">
      <tbody>
        <tr>
          <td className="h-[30px] w-[250px] text-center text-lg">年月</td>
          <td className="h-[30px] text-center text-lg">{section?.name}</td>
        </tr>
        {section?.items?.map((res) => {
          return (
            <tr key={res.id}>
              <td className="w-[250px] text-center text-base">{res.date}</td>
              <td>
                <div dangerouslySetInnerHTML={{ __html: res.summary || "" }} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
  // return (
  //   <Section<Experience> section={section} urlKey="url" summaryKey="summary">
  //     {(item) => (
  //       <div className="flex items-center justify-between">
  //         <div className="text-left">
  //           <div className="text-base font-bold">{item.company}</div>
  //           <div>{item.position}</div>
  //         </div>

  //         <div className="shrink-0 text-right">
  //           <div className="font-bold">{item.date}</div>
  //           <div>{item.location}</div>
  //         </div>
  //       </div>
  //     )}
  //   </Section>
  // );
};

const Education = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.education;
  if (!section?.visible || !section?.items?.length) return null;
  return (
    <table className="w-full table-fixed">
      <tbody>
        <tr>
          <td className="h-[30px] w-[250px] text-center text-lg">年月</td>
          <td className="h-[30px] text-center text-lg">{section?.name}</td>
        </tr>
        {section?.items?.map((res) => {
          return (
            <tr key={res.id}>
              <td className="w-[250px] text-center text-base">{res.date}</td>
              <td>
                <div dangerouslySetInnerHTML={{ __html: res.summary || "" }} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  // return (
  //   <Section<Education> section={section} summaryKey="summary">
  //     {(item) => (
  //       <div className="flex items-center justify-between">
  //         <div className="text-left">
  //           <div className="text-base font-bold">{item.institution}</div>
  //           <div>{item.major}</div>
  //           <div>{item.score}</div>
  //           <div>{item.officialSite}</div>
  //         </div>

  //         <div className="shrink-0 text-right">
  //           <div className="font-bold">{item.date}</div>
  //           <div>{item.studyType}</div>
  //         </div>
  //       </div>
  //     )}
  //   </Section>
  // );
};

const Awards = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.awards;
  if (!section?.visible || !section?.items?.length) return null;
  return (
    <table className="w-full table-fixed">
      <tbody>
        <tr>
          <td className="h-[30px] w-[250px] text-center text-lg">年月</td>
          <td className="h-[30px] text-center text-lg">{section?.name}</td>
        </tr>
        {section?.items?.map((res) => {
          return (
            <tr key={res.id}>
              <td className="w-[250px] text-center text-base">{res.date}</td>
              <td>
                <div dangerouslySetInnerHTML={{ __html: res.summary || "" }} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  // return (
  //   <Section<Award> section={section} urlKey="url" summaryKey="summary">
  //     {(item) => (
  //       <div className="flex items-center justify-between">
  //         <div className="text-left">
  //           <div className="text-base font-bold">{item.title}</div>
  //           <div>{item.awarder}</div>
  //         </div>

  //         <div className="shrink-0 text-right">
  //           <div className="font-bold">{item.date}</div>
  //         </div>
  //       </div>
  //     )}
  //   </Section>
  // );
};

const Certifications = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.certifications;
  if (!section?.visible || !section?.items?.length) return null;

  return (
    <table className="w-full table-fixed">
      <tbody>
        <tr>
          <td className="h-[30px] w-[250px] text-center text-lg">年月</td>
          <td className="h-[30px] text-center text-lg">{section?.name}</td>
        </tr>
        {section?.items?.map((res) => {
          return (
            <tr key={res.id}>
              <td className="w-[250px] text-center text-base">{res.date}</td>
              <td>
                <div dangerouslySetInnerHTML={{ __html: res.summary || "" }} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  // return (
  //   <Section<Certification> section={section} urlKey="url" summaryKey="summary">
  //     {(item) => (
  //       <div className="flex items-center justify-between">
  //         <div className="text-left">
  //           <div className="text-base font-bold">{item.name}</div>
  //           <div>{item.issuer}</div>
  //         </div>

  //         <div className="shrink-0 text-right">
  //           <div className="font-bold">{item.date}</div>
  //         </div>
  //       </div>
  //     )}
  //   </Section>
  // );
};

const Skills = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.skills;
  return (
    <Section<Skill> section={section} levelKey="level" keywordsKey="keywords">
      {(item) => (
        <div>
          <div className="text-base font-bold">{item.name}</div>
          <div>{item.description}</div>
        </div>
      )}
    </Section>
  );
};

const Publications = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.publications;

  if (!section?.visible || !section?.items?.length) return null;
  return (
    <table className="w-full table-fixed">
      <tbody>
        <tr>
          <td className="h-[30px] w-[250px] text-center text-lg">年月</td>
          <td className="h-[30px] text-center text-lg">{section?.name}</td>
        </tr>
        {section?.items?.map((res) => {
          return (
            <tr key={res.id}>
              <td className="w-[250px] text-center text-base">{res.date}</td>
              <td>
                <div dangerouslySetInnerHTML={{ __html: res.summary || "" }} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  // return (
  //   <Section<Publication> section={section} urlKey="url" summaryKey="summary">
  //     {(item) => (
  //       <div className="flex items-center justify-between">
  //         <div className="text-left">
  //           <div className="text-base font-bold">{item.name}</div>
  //           <div>{item.publisher}</div>
  //         </div>

  //         <div className="shrink-0 text-right">
  //           <div className="font-bold">{item.date}</div>
  //         </div>
  //       </div>
  //     )}
  //   </Section>
  // );
};

const Volunteer = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.volunteer;

  if (!section?.visible || !section?.items?.length) return null;
  return (
    <table className="w-full table-fixed">
      <tbody>
        <tr>
          <td className="h-[30px] w-[250px] text-center text-lg">年月</td>
          <td className="h-[30px] text-center text-lg">{section?.name}</td>
        </tr>
        {section?.items?.map((res) => {
          return (
            <tr key={res.id}>
              <td className="w-[250px] text-center text-base">{res.date}</td>
              <td>
                <div dangerouslySetInnerHTML={{ __html: res.summary || "" }} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  // return (
  //   <Section<Volunteer> section={section} urlKey="url" summaryKey="summary">
  //     {(item) => (
  //       <div className="flex items-center justify-between">
  //         <div className="text-left">
  //           <div className="text-base font-bold">{item.organization}</div>
  //           <div>{item.position}</div>
  //         </div>

  //         <div className="shrink-0 text-right">
  //           <div className="font-bold">{item.date}</div>
  //           <div>{item.location}</div>
  //         </div>
  //       </div>
  //     )}
  //   </Section>
  // );
};

const Languages = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.languages;

  return (
    <Section<Language> section={section} levelKey="level">
      {(item) => (
        <div className="space-y-0.5">
          <div className="text-base font-bold">{item.name}</div>
          <div>{item.description}</div>
        </div>
      )}
    </Section>
  );
};

const Projects = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.projects;

  if (!section?.visible || !section?.items?.length) return null;
  return (
    <table className="w-full table-fixed">
      <tbody>
        <tr>
          <td className="h-[30px] w-[250px] text-center text-lg">年月</td>
          <td className="h-[30px] text-center text-lg">{section?.name}</td>
        </tr>
        {section?.items?.map((res) => {
          return (
            <tr key={res.id}>
              <td className="w-[250px] text-center text-base">{res.date}</td>
              <td>
                <div dangerouslySetInnerHTML={{ __html: res.summary || "" }} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  // return (
  //   <Section<Project>
  //     section={section}
  //     urlKey="url"
  //     summaryKey="summary"
  //     keywordsKey="keywords"
  //   >
  //     {(item) => (
  //       <div className="flex items-center justify-between">
  //         <div className="text-left">
  //           <div className="text-base font-bold">{item.name}</div>
  //           <div>{item.description}</div>
  //         </div>

  //         <div className="shrink-0 text-right">
  //           <div className="font-bold">{item.date}</div>
  //         </div>
  //       </div>
  //     )}
  //   </Section>
  // );
};

const References = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.references;

  return (
    <Section<Reference> section={section} urlKey="url" summaryKey="summary">
      {(item) => (
        <div>
          <div className="text-base font-bold">{item.name}</div>
          <div>{item.description}</div>
        </div>
      )}
    </Section>
  );
};

const Custom = ({ id, resume }: { id: string; resume: TResume }) => {
  const section = resume.data.sections?.custom?.[id];

  if (!section?.visible || !section?.items?.length) return null;
  return (
    <table className="w-full table-fixed">
      <tbody>
        <tr>
          <td className="h-[30px] text-left text-lg">{section?.name}</td>
        </tr>
        {section?.items?.map((res) => {
          return (
            <tr key={res.id}>
              <td>
                <div dangerouslySetInnerHTML={{ __html: res.summary || "" }} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <Section<CustomSection>
      section={section}
      urlKey="url"
      summaryKey="summary"
      keywordsKey="keywords"
    >
      {(item) => (
        <div className="flex items-center justify-between">
          <div className="text-left">
            <div className="text-base font-bold">{item.name}</div>
            <div>{item.description}</div>
          </div>

          <div className="shrink-0 text-right">
            <div className="font-bold">{item.date}</div>
            <div>{item.location}</div>
          </div>
        </div>
      )}
    </Section>
  );
};

const mapSectionToComponent = (section: any, resume: TResume) => {
  switch (section) {
    // case "profiles":
    //   return <Profiles resume={resume} />;
    case "summary":
      return <Summary resume={resume} />;
    case "workExperience":
      return <WorkExperience resume={resume} />;
    case "internship":
      return <Internship resume={resume} />;
    case "education":
      return <Education resume={resume} />;
    case "awards":
      return <Awards resume={resume} />;
    case "certifications":
      return <Certifications resume={resume} />;
    // case "skills":
    //   return <Skills resume={resume} />;
    // case "interests":
    //   return <Interests resume={resume} />;
    case "publications":
      return <Publications resume={resume} />;
    case "volunteer":
      return <Volunteer resume={resume} />;
    // case "languages":
    //   return <Languages resume={resume} />;
    case "projects":
      return <Projects resume={resume} />;
    // case "references":
    //   return <References resume={resume} />;
    default:
      if (section.startsWith("custom."))
        return <Custom id={section.split(".")[1]} resume={resume} />;

      return null;
  }
};

export const Rirekisho = ({
  resume,
  columns,
  isFirstPage = false,
}: TemplateProps) => {
  const [main, sidebar] = columns;

  return (
    <div className="space-y-4 p-custom">
      {isFirstPage && <Header resume={resume} />}

      {main.map((section) => (
        <Fragment key={section}>
          {mapSectionToComponent(section, resume)}
        </Fragment>
      ))}

      {sidebar.map((section) => (
        <Fragment key={section}>
          {mapSectionToComponent(section, resume)}
        </Fragment>
      ))}
    </div>
  );
};
